import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";

type Props = {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  testid?: string;
};

export const ImageUpload = ({ value, onChange, label = "Cover image (optional)", testid = "image-upload" }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      return toast.error("Only JPG, PNG, WEBP or GIF images are allowed");
    }
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      onChange(`${API}/media/${data.path}`);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid}>
      <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">{label}</label>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={pick} className="hidden" data-testid={`${testid}-input`} />
      {value ? (
        <div className="mt-2 relative border border-white/10 overflow-hidden group">
          <img src={value} alt="cover" className="w-full h-40 object-cover" data-testid={`${testid}-preview`} />
          <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`}
            className="absolute top-2 right-2 w-8 h-8 grid place-items-center bg-black/70 border border-white/20 hover:border-neon hover:text-neon transition-colors">
            <X size={14} />
          </button>
        </div>
      ) : (
        <button type="button" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testid}-btn`}
          className="mt-2 w-full h-24 border border-dashed border-white/15 flex items-center justify-center gap-3 text-sm text-white/45 hover:border-neon/60 hover:text-white transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={17} className="animate-spin" /> : <ImagePlus size={17} />}
          {busy ? "Uploading…" : "Upload a photo (JPG/PNG/WEBP, max 5 MB)"}
        </button>
      )}
    </div>
  );
};
